import React, { useState } from 'react';
import { siteConfig } from '../data';
import { Battery, Zap, Shield, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const About = () => {
  const icons = [Battery, Zap, Shield, Globe];
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };
  
  const displayedFeatures = showAllFeatures ? siteConfig.features : siteConfig.features.slice(0, 2);

  return (
    <section id="about" className="py-24 md:py-32 lg:py-40 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col xl:flex-row gap-24 xl:gap-32 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="xl:w-1/2 self-start xl:sticky xl:top-32"
          >
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-[0.3em] mb-6">Om GMA</h2>
            <h3 className="text-5xl font-black text-slate-900 mb-8 leading-[1.1] uppercase tracking-tighter">
              Leader of <span className="text-slate-900">Intelligent</span> Energy Industry
            </h3>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed font-medium">
              GMA er dedikeret til at levere de mest avancerede energilagringsløsninger til det danske og europæiske marked. 
              Gennem vores ekspertise og globale netværk, bringer vi verdensførende teknologi direkte til vores kunder.
            </p>
            
            <div className="grid grid-cols-2 gap-12">
              <div>
                <div className="text-5xl font-black text-slate-900 mb-2">100+</div>
                <div className="text-xs text-slate-400 font-bold uppercase tracking-[0.2em]">Eksperter</div>
              </div>
              <div>
                <div className="text-5xl font-black text-slate-900 mb-2">GWh</div>
                <div className="text-xs text-slate-400 font-bold uppercase tracking-[0.2em]">Kapacitet</div>
              </div>
            </div>
          </motion.div>
          
          <div className="xl:w-1/2 grid grid-cols-1 gap-12 md:gap-16 w-full">
            <AnimatePresence mode="popLayout">
              {displayedFeatures.map((feature, index) => {
                const Icon = icons[index % icons.length];
                const isExpanded = expandedIndex === index;

                return (
                  <motion.div 
                    key={index}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.4 }}
                    className="p-10 md:p-12 rounded-none bg-white border-l-4 border-slate-900 shadow-xl shadow-slate-200/50"
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div className="w-14 h-14 bg-slate-900 text-white rounded-none flex items-center justify-center">
                        <Icon size={28} />
                      </div>
                      <button 
                        onClick={() => toggleExpand(index)}
                        className="text-slate-400 hover:text-slate-900 transition-colors p-2"
                        aria-label={isExpanded ? "Vis mindre" : "Læs mere"}
                      >
                        {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                      </button>
                    </div>
                    
                    <h4 className="text-2xl font-black text-slate-900 mb-4 uppercase tracking-tight">{feature.title}</h4>
                    
                    <div className="relative overflow-hidden">
                      <motion.p 
                        layout
                        className={`text-slate-500 text-sm leading-relaxed font-medium ${!isExpanded ? 'line-clamp-2' : ''}`}
                      >
                        {feature.description}
                      </motion.p>
                    </div>

                    <button 
                      onClick={() => toggleExpand(index)}
                      className="mt-6 text-xs font-black uppercase tracking-widest text-slate-900 hover:opacity-70 transition-opacity inline-flex items-center gap-2"
                    >
                      {isExpanded ? "Vis mindre" : "Læs mere"}
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {!showAllFeatures && siteConfig.features.length > 2 && (
              <motion.button
                layout
                onClick={() => setShowAllFeatures(true)}
                className="flex items-center justify-start gap-3 text-slate-500 font-bold uppercase tracking-widest text-sm hover:text-slate-900 transition-colors"
              >
                Se alle områder <ChevronDown size={18} />
              </motion.button>
            )}
            
            <motion.div 
              layout
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-10 md:p-12 rounded-none bg-slate-900 text-white flex flex-col justify-center border-t-4 border-slate-700"
            >
              <h4 className="text-2xl font-black mb-4 uppercase tracking-tight">Vil du vide mere?</h4>
              <p className="text-white/90 text-sm mb-8 font-medium">Kontakt os for en uforpligtende snak om jeres energibehov og fremtidens løsninger.</p>
              <a href="#contact" className="text-sm font-black text-white flex items-center gap-2 group uppercase tracking-widest">
                Kontakt os nu <Zap size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
